import React, {useState, useEffect, useReducer} from 'react'

import {useLoaderData, useNavigate, useSearchParams} from "react-router-dom";
import {Task} from "./Task";
import Button from "./Button";
import {isEmpty} from "radash";
import PipelineInstanceValidationErrorPanel from "./PipelineInstanceValidationErrorPanel";
import { JsonEditor, githubDarkTheme } from 'json-edit-react'
import {matchesSchema} from 'spartan-schema';
import ErrorList from "../widgets/ErrorList";
import {Tabs, Tab} from "../widgets/Tabs";
import Card from "../widgets/Card";
import {FileIcon, FolderIcon} from "../widgets/Icons";
import Link from "../widgets/Link";
import ModalWindow from "../widgets/ModalWindow";
import TaskTable from "./TaskTable";
import {useApi} from "../ApiSession";
import FileManager from "./FileManager";
import {DefaultButton} from "../widgets/Buttons";
import {usePipelineInstanceCustomEditor} from "../PipelineInstanceCustomEditorProvider";
import PipelineInstanceStartButton from "./PipelineInstanceStartButton";


const jsonArgsReducer = (state, action) => {
    switch (action.name) {
        case "init": {
            return {
                args: action.args,
                schema: action.schema,
                errors: []
            }
        }
        case "update": {
            const errors = []
            matchesSchema(state.schema)(action.args, errors)
            if(errors.length === 0) {
                return {
                    ...state,
                    args: action.args,
                    errors: [],
                    isDirty: true
                }
            }
            return {
                ...state,
                errors
            }
        }
        case "save": {
            return {
                ...state,
                isDirty: false
            }
        }
    }
}


export const PipelineInstance = ({}) => {

    const pipelineInstance = useLoaderData()

    const [pipelineState, setPipelineState] = useState(pipelineInstance.state)

    const [pipelineArgsView, pipelineArgsDispatcher] = useReducer(
        jsonArgsReducer,
        jsonArgsReducer(null, {
            name: 'init',
            schema: pipelineInstance.argsSchema,
            args: pipelineInstance.args
        })
    )

    const [searchParams, setSearchParams] = useSearchParams()

    const [selectedTask, setSelectedTask] = useState(null)

    const [fileManagerVisible, setFileManagerVisible] = useState(false)

    const onSelectTask = (taskKey) => setSearchParams({t: taskKey})

    const onCloseTask = () => setSearchParams({})

    const selectedTaskKey = searchParams.get("t")

    const api = useApi()

    const PipelineInstanceCustomEditorForThisType =
        usePipelineInstanceCustomEditor(pipelineInstance.type)

    const [activeTabIdx, setActiveTabIdx] = useState(0)


    useEffect(() => {
        if (selectedTaskKey) {
            api.getTask(pipelineInstance.pid, selectedTaskKey).then(setSelectedTask)
        } else {
            setSelectedTask(null)
        }
    }, [selectedTaskKey]);


    const popSelectedTaskModal = (pipelineInstance) =>
        <ModalWindow
            title={selectedTask.task_key}
            onClose={onCloseTask}
            largeGrid={true}
        >
            <Task
                pipelineInstance={pipelineInstance}
                task={selectedTask}
                onCloseClick={onCloseTask}
            />
        </ModalWindow>


    const fileManagerModal = () => {
        if(! fileManagerVisible) {
            return
        }

        return <ModalWindow
            title={"Manage Pipeline Files"}
            largeGrid={true}
            onClose={() => setFileManagerVisible(false)}
            footer={<>
                <DefaultButton caption={"Close"} onClick={() => setFileManagerVisible(false)}/>
            </>}
        >
            <FileManager pipelineInstanceDir={pipelineInstance.pid}/>
        </ModalWindow>
    }

    const renderActiveTabBody = () => {

        if (activeTabIdx === 0) {

            const renderGenericSettingsEditor = () => <div>
                    <h1>Pipeline Args</h1>
                    <JsonEditor
                        data={pipelineArgsView.args}
                        rootName={""}
                        restrictEdit={pipelineState === "running"}
                        theme={githubDarkTheme}
                        onUpdate={({newData, currentData, newValue, currentValue, name, path}) => {
                            pipelineArgsDispatcher({
                                name: "update",
                                args: newData
                            })
                        }}
                    />
                    {
                        pipelineArgsView.errors && <ErrorList
                            errorMessages={
                                pipelineArgsView.errors.map(e =>
                                    `invalid value for ${e.dataPath.join("/")}: ${e.message}`
                                )
                            }
                        />
                    }
                    {pipelineArgsView.isDirty && <div className={"mt-2"}>
                        <Button
                            caption={"Save"}
                            onClick={
                                () => {
                                    //pipelineArgsDispatcher({name: "saving"})
                                    api.savePipelineInstanceArgs(pipelineInstance.pid, pipelineArgsView.args).then(() =>
                                        pipelineArgsDispatcher({name: "save"})
                                    )
                                }
                            }
                        />
                    </div>}
                    <PipelineInstanceStartButton
                        pipelineInstance={pipelineInstance}
                        isReady={true}
                        onReceiveStartResponse={res => {
                            console.log(res, 0)
                        }}
                    />
                </div>
            return <>
                {
                    PipelineInstanceCustomEditorForThisType ?
                        <PipelineInstanceCustomEditorForThisType
                            pipelineInstance={pipelineInstance}
                            pipelineArgsDispatcher={pipelineArgsDispatcher}
                            renderGeneric={renderGenericSettingsEditor}
                        />
                        :
                        renderGenericSettingsEditor()
                }
            </>
        }

        if (activeTabIdx === 1) {
            return <TaskTable
                pipelineInstance={pipelineInstance}
                tasks={pipelineInstance.tasks}
                onSelectTask={onSelectTask}
                pauseRefresh={!! selectedTaskKey}
            />
        }

        if (activeTabIdx === 2) {
            return <ResultsTab pipelineInstance={pipelineInstance} pipelineState={pipelineState}/>
        }
    }


    const resultsTabCaption = () => {
        return `Results (${pipelineInstance.completedFiles.length})`
    }

    return <>
        {selectedTask && popSelectedTaskModal(pipelineInstance)}
        {fileManagerModal()}
        <Tabs>
            <Tab
                title={"Pipeline Settings"}
                isSelected={activeTabIdx === 0}
                onClick={() => setActiveTabIdx(0)}
            />
            <Tab
                title={"Tasks"}
                isSelected={activeTabIdx === 1}
                onClick={() => setActiveTabIdx(1)}
            />
            <Tab
                title={resultsTabCaption()}
                isSelected={activeTabIdx === 2}
                onClick={() => setActiveTabIdx(2)}
            />
        </Tabs>
        <div className={"p-2"}>
            {renderActiveTabBody()}
        </div>
    </>
}


const ResultsTab = ({pipelineInstance, pipelineState}) => {

    const hasResults = pipelineInstance.completedFiles.length > 0

    if(! hasResults) {
        return <h4>Pipeline has no results yet</h4>
    }

    return <div>
        <table className={"w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400"}>
            <tbody>
            {
                pipelineInstance.completedFiles && pipelineInstance.completedFiles.map(f =>
                    <tr
                        key={f.path}
                        className={
                          "bg-white border-b dark:bg-gray-800 dark:border-gray-700 border-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-default"
                        }
                    >
                        <td><FileIcon/></td>
                        <td>
                            <Link
                                href={`/api/downloadPipelineOutputFile/${pipelineInstance.pid}/${f.path}`}
                                target={"_blank"}
                            >
                                {f.path}
                            </Link>
                        </td>
                </tr>)
            }
            </tbody>
        </table>
    </div>
}